import React from "react";
import moment from "moment";
import { LackOfProsecutionCaseSearchResult } from "../LackOfProsecutionTypes";
import {
  DataTable,
  DataTableColumn,
  Panel,
  Button,
  Row,
  Col,
  NewTabLink,
  ConfirmationModal,
  Icon,
  Checkbox,
  HelpIconMode,
  Tooltip,
} from "@countysuite/ui";
import { LackOfProsecutionAddCaseModal } from "../AddCaseModal/LackOfProsecutionAddCaseModal";
import { HelpIconButton } from "components/HelpIconButton/HelpIconButton";
import { DataTableState } from "@countysuite/ui/components/DataTable/DataTable";

export type Props = {
  redoCaseSearchResults: () => void;
  reloadExistingCaseResults: (
    page: number,
    pageSize: number,
    sorted: any[],
    filtered: any[]
  ) => void;
  addCasesToTable: (addedCases: LackOfProsecutionCaseSearchResult[]) => void;
  casesInTable: LackOfProsecutionCaseSearchResult[];
  nextStep: () => void;
  casePageUrl: string;
  contactPageUrl: string;
  toggleCaseExclusion: (row: LackOfProsecutionCaseSearchResult) => void;
  children?: React.ReactNode;
  totalPages?: number;
  totalRows?: number;
  loading?: boolean;
  showExcludedCases: boolean;
  toggleShowExclusions: (toggled: boolean) => void;
  hasLetters: boolean;
  showParticipantListButton: boolean;
  printParticipantList: () => void;
  exportToCsvUrl: string;
  definitionId: number;
};

type State = {
  typeaheadCaseResults: LackOfProsecutionCaseSearchResult[];
  modalName: Modal;
};

enum Modal {
  NONE = "",
  ADD_ADDITIONAL_CASES = "Add Additional Cases",
  REDO_CASE_SEARCH = "Redo Case Search",
}

export class LackOfProsecutionCaseResultsPanel extends React.Component<
  Props,
  State
> {
  state: State = {
    typeaheadCaseResults: [],
    modalName: Modal.NONE,
  };

  columnDefinitions: DataTableColumn<LackOfProsecutionCaseSearchResult>[] = [
    {
      Header: "Case Number",
      id: "casenumber_col",
      width: 130,
      Cell: (cell) => {
        const row: LackOfProsecutionCaseSearchResult = cell.original;
        const link = `${this.props.casePageUrl}?caseId=${row.caseID}`;
        return (
          <NewTabLink
            href={link}
            className={row.isExcluded ? "excluded" : undefined}
          >
            {row.caseNumber}
          </NewTabLink>
        );
      },
    },
    {
      Header: "Case Caption",
      id: "casecaption_col",
      style: { whiteSpace: "normal" },
      Cell: (cell) => {
        const row: LackOfProsecutionCaseSearchResult = cell.original;
        const link = `${this.props.casePageUrl}?caseId=${row.caseID}`;
        return (
          <NewTabLink
            href={link}
            className={row.isExcluded ? "excluded" : undefined}
          >
            {row.caseCaption}
          </NewTabLink>
        );
      },
    },
    {
      Header: "Case Category",
      id: "casecategory_col",
      style: { whiteSpace: "normal" },
      width: 160,
      key: "caseCategoryName",
      accessor: (row) => row.caseCategoryName || "N/A",
    },
    {
      Header: "Contacts",
      id: "contacts_col",
      style: { whiteSpace: "normal" },
      width: 420,
      sortable: false,
      key: "contacts",
      accessor: (row) => row.contacts,
      Cell: (cell) => {
        const row: LackOfProsecutionCaseSearchResult = cell.original;
        if (!row.contacts || !row.contacts.length) return "No contacts found.";
        const link = `${this.props.casePageUrl}?caseId=${row.caseID}`;
        //build an ordered list with the case.aspx page link

        return (
          <ol className="lop-table-list">
            {row.contacts.map((c) => (
              <li key={c.contactID}>
                <NewTabLink
                  href={link}
                  className={row.isExcluded ? "excluded" : undefined}
                >
                  <span>
                    {c.contactName} ({c.contactType})
                  </span>
                  <div>{c.contactAddress || "No Address"}</div>
                </NewTabLink>
              </li>
            ))}
          </ol>
        );
      },
    },
    {
      Header: "Last Activity",
      id: "lastactivity_col",
      width: 100,
      key: "lastActivityDate",
      accessor: (row) => row.lastActivityDate,
      Cell: (cell) => {
        let css = "centered";
        if (cell.original && cell.original.isExcluded) css = css + " excluded";
        const displayDate = moment(
          cell.original.lastActivityDate,
          "YYYY-MM-DD HH:mm:ss"
        ).format("MM/DD/YYYY");
        const lastActivitySource = cell.original.lastActivityDate
          ? cell.original.lastActivityDate.source
          : "N/A";
        return (
          <Tooltip
            message={`Last Activity Source: ${lastActivitySource}, date: ${displayDate}`}
          >
            <div className={css}>{displayDate}</div>
          </Tooltip>
        );
      },
    },
    {
      Header: "Action",
      id: "actionButtonCol",
      sortable: false,
      width: 110,
      Cell: (cell) => {
        const row: LackOfProsecutionCaseSearchResult = cell.original;

        let toggleBtnContent: JSX.Element;
        if (row.isExcluded) {
          toggleBtnContent = (
            <span>
              <Icon glyph="plus" style={{ color: "forestgreen" }} /> Add
            </span>
          );
        } else {
          toggleBtnContent = (
            <span>
              <Icon glyph="trash" style={{ color: "darkred" }} /> Remove
            </span>
          );
        }

        return (
          <div className="centered">
            <Button onClick={() => this.props.toggleCaseExclusion(row)}>
              {toggleBtnContent}
            </Button>
          </div>
        );
      },
    },
  ];

  showModal = (modalName: Modal) =>
    this.setState({ modalName: modalName || Modal.NONE });

  onFetchData = async (state: DataTableState, globalFilter: string) => {
    this.props.reloadExistingCaseResults(
      state.page,
      state.pageSize,
      state.sorted,
      [{ id: "global", value: globalFilter }]
    );
  };

  toggleShowRemovedCases = (e: any) => {
    const checked = e.target.checked;
    this.props.toggleShowExclusions(checked);
  };

  handleRowStyling = (state: any, rowInfo: any, column: any, instance: any) => {
    if (rowInfo && rowInfo.original && rowInfo.original.isExcluded) {
      return { className: "excluded" };
    }

    return {};
  };

  exportCSV = (e: any) => {
    const exportUrl = `${this.props.exportToCsvUrl}?definitionID=${this.props.definitionId}&includeContactData=false`;
    window.open(exportUrl, "_blank");
  };

  render() {
    const { casesInTable } = this.props;

    let printParticipantListButton = (
      <Button color="primary" onClick={() => this.props.printParticipantList()}>
        Print Participant List
      </Button>
    );
    let newSearchBtn = (
      <Button
        color="success"
        onClick={() => this.showModal(Modal.REDO_CASE_SEARCH)}
      >
        Refresh Cases
      </Button>
    );
    if (this.props.hasLetters) {
      newSearchBtn = (
        <Tooltip
          placement="top"
          message="You cannot do a case search after generating letters.  Please undo all letters first."
        >
          <Button
            color="success"
            onClick={() => this.showModal(Modal.REDO_CASE_SEARCH)}
            disabled
          >
            Refresh Cases
          </Button>
        </Tooltip>
      );
    }

    return (
      <div>
        <LackOfProsecutionAddCaseModal
          onAddCases={this.props.addCasesToTable}
          onHide={() => this.showModal(Modal.NONE)}
          show={this.state.modalName === Modal.ADD_ADDITIONAL_CASES}
        />
        <ConfirmationModal
          size="lg"
          onConfirm={async () => {
            await this.props.redoCaseSearchResults();
            this.showModal(Modal.NONE);
          }}
          onCancel={() => this.showModal(Modal.NONE)}
          show={this.state.modalName === Modal.REDO_CASE_SEARCH}
          title="Redo Case Search Results"
        >
          <p>
            Clicking Confirm will erase the results you currently have. We'll
            take your current selected categories and use them to rebuild your
            case list.
          </p>
          <p>
            Are you <strong>sure</strong> you wish to redo the search? All cases
            that were manually added will be removed.
          </p>
        </ConfirmationModal>

        <Panel
          title="Cases Results"
          buttons={
            <>
              <HelpIconButton mode={HelpIconMode.Modal}>
                <p>
                  Verify that these are the cases you would like to generate
                  letters for.
                </p>
                <p>
                  Once a case has received a letter, you are not allowed to
                  remove it from the list or re-do the search unless you undo
                  the dockets and associated letters on the next step.
                </p>
                <p>
                  If you'd like to add additional cases to the list, use the
                  button in the upper right.
                </p>
                <p>
                  You can always refresh the existing case data by reloading the
                  page or using the Refresh button in the upper right.
                </p>
                <div>
                  If a contact does not show up, please verify that:
                  <ul>
                    <li>
                      The contact is not withdrawn from the case, which is set
                      by the withdrawal date on the Contact page.
                    </li>
                    <li>
                      The contact has at least 1 address marked as primary.
                    </li>
                    <li>
                      The contact is in the category "Plaintiffs" or
                      "Defendants".
                    </li>
                    <li>
                      Please note that plaintiff attorneys and defendant
                      attorneys take precedent over the plaintiffs and
                      defendants themselves. We only show defendants and
                      plaintiffs if they are Pro Se or the attorneys are
                      missing.
                    </li>
                  </ul>
                </div>
              </HelpIconButton>

              <Button onClick={this.exportCSV}>Export Case List to CSV</Button>
              {this.props.showParticipantListButton &&
                printParticipantListButton}
              <Button
                onClick={() => this.showModal(Modal.ADD_ADDITIONAL_CASES)}
              >
                Add Cases
              </Button>
              {newSearchBtn}
            </>
          }
        >
          <Row>
            <Col xs={12}>
              <Checkbox
                name="showRemovedCases"
                onChange={this.toggleShowRemovedCases}
              >
                Show Removed Cases
              </Checkbox>
              <DataTable
                columns={this.columnDefinitions}
                data={casesInTable}
                filterOn={[
                  "caseNumber",
                  "caseCaption",
                  "caseCategoryName",
                  "lastActivityDate",
                ]}
                getRowProps={this.handleRowStyling}
                onFetchData={this.onFetchData}
                fetchDelay={1000}
                pages={this.props.totalPages || 0}
                totalRows={this.props.totalRows}
              />
            </Col>
          </Row>
        </Panel>
      </div>
    );
  }
}

(LackOfProsecutionCaseResultsPanel as any).displayName =
  "LackOfProsecutionCaseResultsPanel";
